/** @prettier */

/**
 * 支払いユーザ譲渡ダイアログ
 */

import React, {useState, useEffect} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  postBillingAuthorizationTransfer,
  getSettingSites,
  deleteBillingAuthorizationTransfer,
  getCompositionsGateway,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {limit} from '../../define';

/**
 * 権限譲渡依頼用ダイアログ
 * @param gwID ゲートウェイID
 * @param userID ユーザID
 * @param userEmail E-mailアドレス
 * @param transferUser 権限譲渡するユーザ
 * @param setGWInfo ゲートウェイ情報の更新関数
 * @param isOpenDialog ダイアログオープン
 * @param setIsOpenDialog ダイアログオープン更新関数
 * @param setMessage 確認メッセージ更新関数
 * @param setIsTransferMessageDialog 確認メッセージダイアログ更新関数
 * @returns JSX
 */
const AuthorizationTransferDialog = (props: {
  gwID: string;
  userID: string;
  userEmail: string;
  transferUser: string;
  setGWInfo: (arg0: apiIF.res.getCompositionsGateway) => void;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  setIsTransferMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();

  const [usersAuthority, setUsersAuthority] = useState<
    apiIF.resParameters.userAuthority[] | null
  >(null); //ユーザーの権限情報（ログインしているユーザ以外）
  const [selectUser, setSelectUser] = useState(props.transferUser); //選択したユーザ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    (async () => {
      try {
        if (props.gwID === undefined || siteID === undefined) return;
        //Adminのメールアドレス一覧を取得
        const response = await getSettingSites(siteID, {
          'max-result': limit.REGISTRATION_NUM_AUTHORITY_MAX,
        });
        const tempUsersAuthority = response['users-authority'].filter(info => {
          return (
            info['e-mail'] !== props.userEmail &&
            info['site-authority'] === 'admin'
          );
        });
        setUsersAuthority(tempUsersAuthority);
      } catch {
        setIsOpenAPIErrorDialog(true);
      }
    })();
  }, []);

  //入力項目のチェック
  useEffect(() => {
    if (selectUser === '' || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [selectUser, apiWait]);

  /**
   * SAVEボタン押下時処理
   */
  const authorizationTransfer = async () => {
    try {
      //パスパラメータを取得できない場合
      if (siteID === undefined || props.gwID === undefined) return;
      setAPIWait(true);
      // 支払いユーザの譲渡先を登録する
      await postBillingAuthorizationTransfer(siteID, props.gwID, {
        'transfer-user-id': selectUser,
      });
      // ゲートウェイ情報の更新
      const gwInfos = await getCompositionsGateway(siteID, {'max-result': 10});
      props.setGWInfo(gwInfos);

      //依頼完了をダイアログで表示
      props.setMessage(
        `${trans('AuthorizationTransferDialog.message.request')}`
      );
      props.setIsTransferMessageDialog(true);
      // ダイアログを閉じる
      props.setIsOpenDialog(false);
    } catch {
      //APIエラーがダイアログの表示
      setIsOpenAPIErrorDialog(true);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * REQUEST CANCELボタン押下時処理
   */
  const authorizationTransferCancel = async () => {
    try {
      //パスパラメータを取得できない場合
      if (siteID === undefined || props.gwID === undefined) return;
      setAPIWait(true);
      // 支払いユーザの譲渡先を削除する
      await deleteBillingAuthorizationTransfer(siteID, props.gwID);
      // ゲートウェイ情報の更新
      const gwInfos = await getCompositionsGateway(siteID, {'max-result': 10});
      props.setGWInfo(gwInfos);

      //キャンセル完了をダイアログで表示
      props.setMessage(
        `${trans('AuthorizationTransferDialog.message.cancel')}`
      );
      props.setIsTransferMessageDialog(true);
      // ダイアログを閉じる
      props.setIsOpenDialog(false);
    } catch {
      setIsOpenAPIErrorDialog(true);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) return <></>;

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('AuthorizationTransferDialog.title')}</DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('AuthorizationTransferDialog.msg')}
          </DialogContentText>
          <FormControl>
            <RadioGroup
              aria-labelledby="user-radio-buttons-group-label"
              name="radio-buttons-group">
              {usersAuthority !== null ? (
                <>
                  {usersAuthority.map(
                    (authority: apiIF.resParameters.userAuthority) => {
                      return (
                        <FormControlLabel
                          key={authority['user-id']}
                          value={authority['user-id']}
                          control={<Radio />}
                          label={authority['e-mail']}
                          onChange={() => setSelectUser(authority['user-id'])}
                          // 譲渡するユーザと異なるユーザの場合はグレーアウトする
                          disabled={
                            props.transferUser !== '' &&
                            authority['user-id'] !== props.transferUser
                          }
                          checked={authority['user-id'] === selectUser}
                        />
                      );
                    }
                  )}
                </>
              ) : (
                <Box className="transferDialog__skeleton__area">
                  {[...Array(3)].map((_, index) => {
                    return (
                      <Box
                        key={`skeleton${index}`}
                        className="transferDialog__skeleton__row">
                        <Skeleton animation="wave" width={320} height={30} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('AuthorizationTransferDialog.buttonTxt.cancel')}
          />
          {/* REQUEST/REQUEST CANCELボタンの設定） */}
          {props.transferUser === '' ? (
            <PrimaryButton
              onClick={() => authorizationTransfer()}
              buttonText={trans(
                'AuthorizationTransferDialog.buttonTxt.request'
              )}
              disabled={okBtnDisabled}
              apiWait={apiWait}
            />
          ) : (
            <PrimaryButton
              onClick={() => authorizationTransferCancel()}
              buttonText={trans(
                'AuthorizationTransferDialog.buttonTxt.requestCancel'
              )}
              disabled={okBtnDisabled}
              apiWait={apiWait}
            />
          )}
        </DialogActions>
      </Dialog>
      {/* エラー表示 */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
        id={`EditErrDlgBtn`}
      />
    </>
  );
};
export default AuthorizationTransferDialog;
